import { useEffect, useRef } from 'react';
import { Form, Input, Select, Divider } from '@bancobarigui/barihaus';

import { AvaliacaoData, WizardMode } from '@/features/laudo/types/laudo-wizard.types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { responseService } from '@/services/checklists/response.service';
import { QuestionResponse } from '@/features/laudo/types/checklist-response.types';
import { ReviewFieldWrapper } from '../../ReviewFieldWrapper';
import { useReviewMode } from '../../index';
import { usePropertyTypeOptions } from '@/features/laudo/hooks/usePropertyTypeOptions';
import { LIQUIDEZ_OBRIGATORIA_MENSAGEM, LIQUIDEZ_OPTIONS, parseLiquidez } from '@/features/laudo/utils/liquidez';

interface AvaliacaoStepProps {
  data: AvaliacaoData;
  onUpdate: (data: Partial<AvaliacaoData>) => void;
  inspection: InspectionRequest;
  mode?: WizardMode;
}

// Opções para os dropdowns

const TIPO_IMPLANTACAO_OPTIONS = [
  { label: 'Isolado', value: 'Isolado', disabled: false },
  { label: 'Condomínio', value: 'Condomínio', disabled: false },
];

const ESTADO_CONSERVACAO_OPTIONS = [
  { label: 'Nova (até 5 anos)', value: 'Nova(até 5 anos)', disabled: false },
  { label: 'Nova | Regular', value: 'Nova|Regular', disabled: false },
  { label: 'Regular', value: 'Regular', disabled: false },
  { label: 'Regular | Reparos simples', value: 'Regular|Reparos simples', disabled: false },
  { label: 'Reparos simples', value: 'Reparos simples', disabled: false },
  { label: 'Reparos simples | Reparos importantes', value: 'Reparos simples|Reparos importantes', disabled: false },
  { label: 'Reparos importantes', value: 'Reparos importantes', disabled: false },
  { label: 'Reparos importantes | Sem valor', value: 'Reparos importantes|Sem valor', disabled: false },
  { label: 'Sem valor', value: 'Sem valor', disabled: false },
];

const PADRAO_ACABAMENTO_OPTIONS = [
  { label: 'Alto', value: 'Alto', disabled: false },
  { label: 'Normal', value: 'Normal', disabled: false },
  { label: 'Baixo', value: 'Baixo', disabled: false },
  { label: 'Mínimo', value: 'Mínimo', disabled: false },
];

const PADRAO_TERRENO_OPTIONS = [
  { label: 'Alto', value: 'Alto', disabled: false },
  { label: 'Médio', value: 'Médio', disabled: false },
  { label: 'Baixo', value: 'Baixo', disabled: false },
];

const TOPOGRAFIA_OPTIONS = [
  { label: 'Plano', value: 'Plano', disabled: false },
  { label: 'Declive', value: 'Declive', disabled: false },
  { label: 'Aclive', value: 'Aclive', disabled: false },
];

/**
 * Normaliza texto para busca (remove acentos, lowercase, remove caracteres especiais)
 */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[^a-z0-9\s]/g, '') // Remove caracteres especiais
    .trim();
}

/**
 * Verifica se o texto contém alguma das palavras-chave
 * Agora com busca mais flexível: procura por palavras individuais também
 */
function containsKeywords(text: string, keywords: string[]): boolean {
  const normalized = normalizeText(text);

  for (const keyword of keywords) {
    const normalizedKeyword = normalizeText(keyword);

    // 1. Verifica match exato da keyword completa
    if (normalized.includes(normalizedKeyword)) {
      return true;
    }

    // 2. Verifica se todas as palavras da keyword existem no texto (em qualquer ordem)
    const keywordWords = normalizedKeyword.split(/\s+/).filter(w => w.length > 2); // ignora palavras muito pequenas como "de", "do"
    const textWords = normalized.split(/\s+/);

    // Se a keyword tem apenas 1 palavra com mais de 2 caracteres, aceita match parcial mais flexível
    if (keywordWords.length === 1) {
      const singleKeyword = keywordWords[0];
      const hasMatch = textWords.some(textWord =>
        textWord.includes(singleKeyword) ||
        singleKeyword.includes(textWord) ||
        (singleKeyword.length >= 4 && textWord.length >= 4 &&
         (textWord.startsWith(singleKeyword.slice(0, 4)) || singleKeyword.startsWith(textWord.slice(0, 4))))
      );

      if (hasMatch) {
        return true;
      }
    }

    // Para keywords com múltiplas palavras, todas devem existir
    if (keywordWords.length > 1) {
      const allWordsMatch = keywordWords.every(keywordWord =>
        textWords.some(textWord =>
          textWord.includes(keywordWord) ||
          (textWord.length >= 4 && keywordWord.includes(textWord))
        )
      );

      if (allWordsMatch) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Extrai valor numérico de uma resposta
 */
function extractNumericValue(response: any): number | null {
  if (!response || response.isNotApplicable) {
    return null;
  }

  // Numeric answer direto
  if (typeof response.numericValue === 'number') {
    return response.numericValue;
  }

  // Text answer que pode conter número
  if (response.textValue) {
    const match = response.textValue.match(/(\d+(?:[.,]\d+)?)/);
    if (match) {
      const value = parseFloat(match[1].replace(',', '.'));
      return value;
    }
  }

  // Selection answer que pode conter número
  if (response.selectedOption) {
    const match = response.selectedOption.match(/(\d+(?:[.,]\d+)?)/);
    if (match) {
      const value = parseFloat(match[1].replace(',', '.'));
      return value;
    }
  }

  // Fallback: tenta buscar em qualquer propriedade do objeto
  const allValues = Object.values(response);
  for (const val of allValues) {
    if (typeof val === 'number' && !isNaN(val)) {
      return val;
    }
    if (typeof val === 'string') {
      const match = val.match(/(\d+(?:[.,]\d+)?)/);
      if (match) {
        const value = parseFloat(match[1].replace(',', '.'));
        return value;
      }
    }
  }

  return null;
}

/**
 * Extrai valor de seleção de uma resposta
 */
function extractSelectionValue(response: any): string | null {
  if (!response || response.isNotApplicable) {
    return null;
  }

  if (response.selectedOption) {
    return response.selectedOption;
  }

  if (response.textValue) {
    return response.textValue;
  }

  // Fallback: tenta buscar em qualquer propriedade string não-vazia
  const allValues = Object.values(response);
  for (const val of allValues) {
    if (typeof val === 'string' && val.trim().length > 0 && val !== 'false' && val !== 'true') {
      return val;
    }
  }

  return null;
}

/**
 * Valida se o valor extraído existe nas opções do dropdown
 * Se não existir, tenta encontrar uma opção similar usando fuzzy matching
 */
function validateAndMatchDropdownValue(
  extractedValue: string | null,
  options: Array<{ label: string; value: string; disabled: boolean }>,
  fieldName: string
): string | null {
  if (!extractedValue) return null;

  // 1. Verifica se o valor existe exatamente nas opções
  const exactMatch = options.find(opt => opt.value === extractedValue);
  if (exactMatch) {
    return extractedValue;
  }

  // 2. Tenta fuzzy matching (normalizado)
  const normalizedExtracted = normalizeText(extractedValue);
  for (const option of options) {
    const normalizedOption = normalizeText(option.value);

    // Match exato normalizado
    if (normalizedExtracted === normalizedOption) {
      return option.value;
    }

    // Match parcial (contém)
    if (normalizedExtracted.includes(normalizedOption) || normalizedOption.includes(normalizedExtracted)) {
      return option.value;
    }
  }

  return null;
}

/**
 * Busca valores do checklist que podem corresponder aos parâmetros
 * usando busca fuzzy/LIKE para encontrar campos com nomes similares
 */
async function extractParametersFromChecklist(inspection: InspectionRequest, tipoImovelOptions: { label: string; value: string; disabled: boolean }[] = []): Promise<Partial<AvaliacaoData>> {
  const firstItem = inspection.items?.[0];
  if (!firstItem || !firstItem.checklists || firstItem.checklists.length === 0) {
    return {};
  }

  const result: Partial<AvaliacaoData> = {
    dadosImovel: {} as any,
    dadosTerreno: {} as any,
    dadosCondominio: {} as any,
  };

  // Mapeamento de palavras-chave para campos
  // IMPORTANTE: Colocar keywords mais específicas PRIMEIRO, depois genéricas
  // Ordem: específica -> genérica (para evitar matches incorretos)
  const keywords = {
    // Dados do Imóvel
    tipoImovel: ['tipo imovel', 'tipo do imovel', 'classificacao imovel', 'categoria imovel', 'finalidade imovel'],
    tipoImplantacao: ['tipo implantacao', 'implantacao', 'forma implantacao', 'tipo edificacao'],
    areaConstruida: ['area privativa', 'area construida', 'area util', 'area edificacao', 'metragem construida', 'metragem privativa', 'area total construida', 'area interna'],
    quantidadeQuartos: ['quantidade quartos', 'quartos', 'dormitorios', 'numero quartos', 'qtd quartos', 'qtde quartos', 'numero dormitorios'],
    quantidadeBanheiros: ['quantidade banheiros', 'banheiros', 'numero banheiros', 'qtd banheiros', 'qtde banheiros', 'sanitarios', 'numero sanitarios'],
    quantidadeVagas: ['quantidade vagas', 'vagas', 'garagem', 'numero vagas', 'qtd vagas', 'qtde vagas', 'vagas garagem', 'estacionamento'],
    idadeAparente: ['idade aparente', 'anos construcao', 'tempo construcao', 'ano edificacao', 'ano imovel', 'idade imovel', 'data construcao'],
    // Estado de conservação: versões específicas PRIMEIRO, genéricas DEPOIS
    estadoConservacao: ['estado conservacao imovel', 'conservacao imovel', 'condicao imovel', 'estado imovel', 'conservacao unidade', 'estado unidade', 'estado conservacao', 'conservacao'],
    padraoAcabamento: ['padrao acabamento', 'acabamento imovel', 'padrao construtivo', 'qualidade acabamento', 'nivel acabamento', 'acabamento unidade', 'acabamento'],
    fatorLiquidez: ['fator liquidez', 'fator de liquidez', 'coeficiente liquidez', 'indice liquidez', 'liquidez imovel', 'liquidez'],

    // Dados do Terreno
    areaTerreno: ['area terreno', 'area lote', 'metragem terreno', 'metragem lote', 'tamanho terreno', 'dimensao terreno'],
    testada: ['testada', 'frente terreno', 'largura frente', 'testada principal', 'medida testada'],
    topografia: ['topografia', 'relevo terreno', 'inclinacao terreno', 'declive terreno', 'topografia lote'],
    padraoTerreno: ['padrao terreno', 'qualidade terreno', 'padrao lote', 'classificacao terreno'],

    // Dados do Condomínio - IMPORTANTE: checar condomínio ANTES de checar imóvel genérico
    estadoConservacaoCondominio: ['estado conservacao condominio', 'conservacao condominio', 'condicao condominio', 'estado edificio', 'conservacao edificio', 'estado predial'],
    areaComum: ['area comum', 'area condominial', 'areas comuns', 'area lazer', 'espaco comum', 'area comum condominio'],
  };

  try {
    // Buscar responses do checklist
    const checklistId = firstItem.checklists[0].checklistId;
    const inspectionId = inspection.id;

    const responses = await responseService.getResponsesByInspectionId(checklistId, inspectionId);
    const responsesArray = Array.isArray(responses) ? responses : [];

    (responsesArray as unknown as QuestionResponse[]).forEach((response: QuestionResponse) => {
      const questionTitle = response.question.title;

      // Dados do Imóvel
      if (containsKeywords(questionTitle, keywords.tipoImovel)) {
        const extractedValue = extractSelectionValue(response.answer);
        const validatedValue = validateAndMatchDropdownValue(extractedValue, tipoImovelOptions, 'Tipo de Imóvel');
        if (validatedValue && !result.dadosImovel!.tipoImovel) {
          result.dadosImovel!.tipoImovel = validatedValue;
        }
      }

      if (containsKeywords(questionTitle, keywords.tipoImplantacao)) {
        const extractedValue = extractSelectionValue(response.answer);
        let finalValue = validateAndMatchDropdownValue(extractedValue, TIPO_IMPLANTACAO_OPTIONS, 'Tipo de Implantação');
        // Normaliza valores antigos para as novas opções
        if (!finalValue && extractedValue) {
          const normalized = extractedValue.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          if (normalized.includes('condominio') || normalized.includes('edificio') || normalized.includes('geminado')) {
            finalValue = 'Condomínio';
          }
        }
        if (finalValue && !result.dadosImovel!.tipoImplantacao) {
          result.dadosImovel!.tipoImplantacao = finalValue;
        }
      }

      if (containsKeywords(questionTitle, keywords.areaConstruida)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosImovel!.areaConstruida) {
          result.dadosImovel!.areaConstruida = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.quantidadeQuartos)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosImovel!.quantidadeQuartos) {
          result.dadosImovel!.quantidadeQuartos = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.quantidadeBanheiros)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosImovel!.quantidadeBanheiros) {
          result.dadosImovel!.quantidadeBanheiros = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.quantidadeVagas)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosImovel!.quantidadeVagas) {
          result.dadosImovel!.quantidadeVagas = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.idadeAparente)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosImovel!.idadeAparente) {
          result.dadosImovel!.idadeAparente = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.estadoConservacao)) {
        const extractedValue = extractSelectionValue(response.answer);
        const validatedValue = validateAndMatchDropdownValue(extractedValue, ESTADO_CONSERVACAO_OPTIONS, 'Estado de Conservação');
        if (validatedValue && !result.dadosImovel!.estadoConservacao) {
          result.dadosImovel!.estadoConservacao = validatedValue;
        }
      }

      if (containsKeywords(questionTitle, keywords.padraoAcabamento)) {
        const extractedValue = extractSelectionValue(response.answer);
        const validatedValue = validateAndMatchDropdownValue(extractedValue, PADRAO_ACABAMENTO_OPTIONS, 'Padrão de Acabamento');
        if (validatedValue && !result.dadosImovel!.padraoAcabamento) {
          result.dadosImovel!.padraoAcabamento = validatedValue;
        }
      }

      if (containsKeywords(questionTitle, keywords.fatorLiquidez)) {
        // Só aproveita a resposta se ela for exatamente uma das opções (sem diferenciar acento/caixa).
        const value = parseLiquidez(extractSelectionValue(response.answer));
        if (value && result.dadosImovel!.fatorLiquidez === undefined) {
          result.dadosImovel!.fatorLiquidez = value;
        }
      }

      // Dados do Terreno
      if (containsKeywords(questionTitle, keywords.areaTerreno)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosTerreno!.areaTerreno) {
          result.dadosTerreno!.areaTerreno = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.testada)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosTerreno!.testada) {
          result.dadosTerreno!.testada = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.topografia)) {
        const extractedValue = extractSelectionValue(response.answer);
        const validatedValue = validateAndMatchDropdownValue(extractedValue, TOPOGRAFIA_OPTIONS, 'Topografia');
        if (validatedValue && !result.dadosTerreno!.topografia) {
          result.dadosTerreno!.topografia = validatedValue;
        }
      }

      if (containsKeywords(questionTitle, keywords.padraoTerreno)) {
        const extractedValue = extractSelectionValue(response.answer);
        const validatedValue = validateAndMatchDropdownValue(extractedValue, PADRAO_TERRENO_OPTIONS, 'Padrão do Terreno');
        if (validatedValue && !result.dadosTerreno!.padraoTerreno) {
          result.dadosTerreno!.padraoTerreno = validatedValue;
        }
      }

      // Dados do Condomínio
      if (containsKeywords(questionTitle, keywords.areaComum)) {
        const value = extractNumericValue(response.answer);
        if (value !== null && !result.dadosCondominio!.areaComum) {
          result.dadosCondominio!.areaComum = value;
        }
      }

      if (containsKeywords(questionTitle, keywords.estadoConservacaoCondominio)) {
        const extractedValue = extractSelectionValue(response.answer);
        const validatedValue = validateAndMatchDropdownValue(extractedValue, ESTADO_CONSERVACAO_OPTIONS, 'Estado de Conservação (Condomínio)');
        if (validatedValue && !result.dadosCondominio!.estadoConservacao) {
          result.dadosCondominio!.estadoConservacao = validatedValue;
        }
      }
    });
  } catch {
    // Error extracting parameters from checklist
  }

  return result;
}

export function AvaliacaoStep({ data, onUpdate, inspection, mode = 'create' }: AvaliacaoStepProps) {
  const isReviewMode = mode === 'review';
  const isCorrectionMode = mode === 'correction';
  const { reviewContext } = useReviewMode();

  // Correções pendentes
  const pendingCorrections = reviewContext?.pendingCorrections || [];

  // Detecta se é uma devolução externa (sem correções específicas)
  const isExternalReturn = isCorrectionMode && (!reviewContext?.pendingCorrections || reviewContext.pendingCorrections.length === 0);

  // Verifica se "Tipo de Imóvel" está marcado para correção
  // Quando "Tipo de Imóvel" está em correção, TODOS os campos do step de Avaliação são liberados
  // (porque mudar o tipo de imóvel geralmente afeta área, quartos, terreno, etc.)
  const hasTipoImovelCorrection = isExternalReturn || (isCorrectionMode && pendingCorrections.some(
    c => c?.fieldPath === 'avaliacao.dadosImovel.tipoImovel'
  ));

  // Helper para verificar se um campo específico está habilitado no modo correção
  const isFieldEnabled = (fieldPath: string) => {
    if (isReviewMode) return false; // Modo revisão sempre desabilita
    if (!isCorrectionMode) return true; // Modo create/edit sempre habilita

    // Se é devolução externa, todos os campos são editáveis
    if (isExternalReturn) return true;

    // Se "Tipo de Imóvel" está em correção, libera TODOS os campos do step de Avaliação
    if (hasTipoImovelCorrection) return true;

    // Verifica se o campo específico está marcado para correção
    return pendingCorrections.some(c => c?.fieldPath === fieldPath);
  };

  const [form] = Form.useForm();
  // Laudos anteriores à lista fechada chegam sem liquidez válida (vazio ou o fator numérico antigo).
  // Como o campo é obrigatório para finalizar, numa correção ele fica liberado mesmo sem ter sido
  // apontado pelo revisor; senão o avaliador não conseguiria avançar. Calculado uma vez, na montagem,
  // para o campo não travar de novo logo depois que o avaliador escolhe uma opção.
  const liquidezPendenteRef = useRef(parseLiquidez(data.dadosImovel?.fatorLiquidez) === undefined);
  const liberarLiquidezNaCorrecao = isCorrectionMode && liquidezPendenteRef.current;
  const { options: tipoImovelOptions } = usePropertyTypeOptions();
  const tipoImovel = Form.useWatch(['dadosImovel', 'tipoImovel'], form);
  const isTerreno = tipoImovel === 'Terreno';
  const tipoImplantacao = Form.useWatch(['dadosImovel', 'tipoImplantacao'], form);
  const isCondominio = tipoImplantacao === 'Condomínio';
  // Sem liquidez o "Próximo" fica desabilitado; a mensagem embaixo do campo diz o porquê.
  const liquidezAtual = Form.useWatch(['dadosImovel', 'fatorLiquidez'], form);
  const avisarLiquidezVazia = !isReviewMode && !parseLiquidez(liquidezAtual);
  const isInitializedRef = useRef(false); // Flag para evitar sync durante inicialização
  const onUpdateRef = useRef(onUpdate); // Ref para a função onUpdate (para usar no cleanup)
  // Ref com os dados COMPLETOS do form (incluindo valores do parent)
  const fullDataRef = useRef<AvaliacaoData | null>(null);
  // Ref para guardar mudanças do usuário durante carregamento async
  const userChangesRef = useRef<Record<string, any>>({});
  // Ref para saber se está carregando defaults (para buffer de mudanças)
  const isLoadingDefaultsRef = useRef(false);

  // Atualiza a ref do onUpdate
  onUpdateRef.current = onUpdate;

  // Limpa dados do condomínio ao trocar tipo de implantação
  useEffect(() => {
    if (!isCondominio && isInitializedRef.current) {
      form.setFieldsValue({
        dadosCondominio: { areaComum: 0, estadoConservacao: '' },
      });
    }
  }, [isCondominio, form]);

  // Remove valores undefined para não sobrescrever dados existentes
  const cleanObject = (obj: any) => {
    if (!obj || typeof obj !== 'object') return {};
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== undefined)
    );
  };

  /**
   * Cria objeto de dados atualizado mesclando dados base com valores do form
   */
  const buildUpdatedData = (baseData: AvaliacaoData, formValues: any): AvaliacaoData => {
    return {
      dadosImovel: {
        ...baseData.dadosImovel,
        ...cleanObject(formValues?.dadosImovel)
      },
      dadosTerreno: {
        ...baseData.dadosTerreno,
        ...cleanObject(formValues?.dadosTerreno)
      },
      dadosCondominio: {
        ...baseData.dadosCondominio,
        ...cleanObject(formValues?.dadosCondominio)
      },
      _initialized: true,
    };
  };

  /**
   * Handler para sincronização IMEDIATA quando qualquer valor do form muda
   */
  const handleValuesChange = (_changedValues: any, allValues: any) => {
    // Se está carregando defaults, guarda as mudanças do usuário para aplicar depois
    if (isLoadingDefaultsRef.current) {
      // Deep merge das mudanças
      if (_changedValues.dadosImovel) {
        userChangesRef.current['dadosImovel'] = {
          ...(userChangesRef.current['dadosImovel'] || {}),
          ..._changedValues.dadosImovel,
        };
      }
      if (_changedValues.dadosTerreno) {
        userChangesRef.current['dadosTerreno'] = {
          ...(userChangesRef.current['dadosTerreno'] || {}),
          ..._changedValues.dadosTerreno,
        };
      }
      if (_changedValues.dadosCondominio) {
        userChangesRef.current['dadosCondominio'] = {
          ...(userChangesRef.current['dadosCondominio'] || {}),
          ..._changedValues.dadosCondominio,
        };
      }
      return;
    }

    // Não sincroniza se ainda não foi inicializado (e não está carregando)
    if (!isInitializedRef.current) {
      return;
    }

    // Usa fullDataRef como base (que tem os dados mais recentes)
    const baseData = fullDataRef.current || data;
    const updatedData = buildUpdatedData(baseData, allValues);

    // Atualiza fullDataRef com os novos dados
    fullDataRef.current = updatedData;

    onUpdate(updatedData);
  };

  const lastExternalTipoImovelRef = useRef(data.dadosImovel?.tipoImovel || '');
  useEffect(() => {
    const externalTipoImovel = data.dadosImovel?.tipoImovel || '';
    if (isInitializedRef.current && externalTipoImovel !== lastExternalTipoImovelRef.current) {
      lastExternalTipoImovelRef.current = externalTipoImovel;
      form.setFieldValue(['dadosImovel', 'tipoImovel'], externalTipoImovel);
      if (fullDataRef.current) {
        fullDataRef.current = {
          ...fullDataRef.current,
          dadosImovel: {
            ...fullDataRef.current.dadosImovel,
            tipoImovel: externalTipoImovel,
          },
        };
      }
    }
  }, [data.dadosImovel?.tipoImovel, form]);

  // Sincronização ao DESMONTAR o componente (garante que dados não sejam perdidos)
  useEffect(() => {
    return () => {
      // Cleanup: sincroniza dados COMPLETOS com o parent ao desmontar
      if (isInitializedRef.current && fullDataRef.current) {
        onUpdateRef.current(fullDataRef.current);
      }
    };
  }, []);

  // Sincroniza o form com dados do parent na montagem
  useEffect(() => {
    // Verifica se já tem dados do usuário no state do parent
    const hasUserData = !!(
      // Dados do Imóvel
      data.dadosImovel?.tipoImovel ||
      data.dadosImovel?.tipoImplantacao ||
      data.dadosImovel?.estadoConservacao ||
      data.dadosImovel?.padraoAcabamento ||
      (data.dadosImovel?.areaConstruida !== undefined && data.dadosImovel.areaConstruida >= 0) ||
      (data.dadosImovel?.quantidadeQuartos !== undefined && data.dadosImovel.quantidadeQuartos > 0) ||
      (data.dadosImovel?.quantidadeBanheiros !== undefined && data.dadosImovel.quantidadeBanheiros > 0) ||
      (data.dadosImovel?.quantidadeVagas !== undefined && data.dadosImovel.quantidadeVagas > 0) ||
      (data.dadosImovel?.idadeAparente !== undefined && data.dadosImovel.idadeAparente > 0) ||
      // Dados do Terreno
      (data.dadosTerreno?.areaTerreno && data.dadosTerreno.areaTerreno > 0) ||
      (data.dadosTerreno?.testada && data.dadosTerreno.testada > 0) ||
      data.dadosTerreno?.padraoTerreno ||
      data.dadosTerreno?.topografia ||
      // Dados do Condomínio
      (data.dadosCondominio?.areaComum && data.dadosCondominio.areaComum > 0) ||
      data.dadosCondominio?.estadoConservacao
    );

    // Se já foi inicializado com dados do checklist, apenas restaura no form
    // IMPORTANTE: Removemos a verificação hasUserData para SEMPRE buscar do checklist quando não foi inicializado
    if (data._initialized) {
      // Converter valores numéricos para string (para compatibilidade com Select do barihaus)
      const formData = {
        dadosImovel: {
          ...data.dadosImovel,
          areaConstruida: data.dadosImovel?.areaConstruida ?? undefined,
          quantidadeQuartos: data.dadosImovel?.quantidadeQuartos ? String(data.dadosImovel.quantidadeQuartos) : undefined,
          quantidadeBanheiros: data.dadosImovel?.quantidadeBanheiros ? String(data.dadosImovel.quantidadeBanheiros) : undefined,
          quantidadeVagas: data.dadosImovel?.quantidadeVagas ? String(data.dadosImovel.quantidadeVagas) : undefined,
          idadeAparente: data.dadosImovel?.idadeAparente || undefined,
          fatorLiquidez: parseLiquidez(data.dadosImovel?.fatorLiquidez),
        },
        dadosTerreno: {
          ...data.dadosTerreno,
          areaTerreno: data.dadosTerreno?.areaTerreno || undefined,
          testada: data.dadosTerreno?.testada || undefined,
        },
        dadosCondominio: {
          ...data.dadosCondominio,
          areaComum: data.dadosCondominio?.areaComum || undefined,
        },
      };
      form.setFieldsValue(formData);
      // Atualiza fullDataRef com os dados COMPLETOS (incluindo _initialized)
      fullDataRef.current = {
        ...data,
        _initialized: true,
      };
      // Habilita sincronização IMEDIATAMENTE (sem setTimeout)
      isInitializedRef.current = true;
    } else {
      // SEMPRE carrega defaults do checklist quando não foi inicializado
      // A função preservará valores já preenchidos pelo usuário
      loadChecklistDefaults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Função para carregar defaults do checklist
  // IMPORTANTE: Esta função só é chamada quando NÃO há dados existentes
  // Ela mescla defaults do checklist com valores existentes (preservando valores já preenchidos)
  const loadChecklistDefaults = async () => {
    // Marca que está carregando - mudanças do usuário serão bufferizadas
    isLoadingDefaultsRef.current = true;

    try {
      const firstItem = inspection.items?.[0];
      const checklistValues = await extractParametersFromChecklist(inspection, tipoImovelOptions);

      // Helper para preservar valor existente OU usar fallback
      const preserveOrDefault = <T,>(existingValue: T | undefined | null, fallback: T): T => {
        // Para strings, verifica se não está vazia
        if (typeof existingValue === 'string') {
          return existingValue.trim() !== '' ? existingValue : fallback;
        }
        // Para números, verifica se é válido (aceita 0 como valor válido)
        if (typeof existingValue === 'number') {
          return !isNaN(existingValue) ? existingValue : fallback;
        }
        // Para outros tipos, verifica se não é null/undefined
        return existingValue !== undefined && existingValue !== null ? existingValue : fallback;
      };

      // Helper para converter número para string (para Selects que esperam string)
      const toStringValue = (val: any): string => {
        if (val === undefined || val === null) return '0';
        return String(val);
      };

      const merged: AvaliacaoData = {
        dadosImovel: {
          ...data.dadosImovel,
          areaConstruida: preserveOrDefault(data.dadosImovel?.areaConstruida, firstItem?.builtArea || firstItem?.privateArea || 0),
          tipoImovel: preserveOrDefault(data.dadosImovel?.tipoImovel, checklistValues.dadosImovel?.tipoImovel || ''),
          tipoImplantacao: preserveOrDefault(data.dadosImovel?.tipoImplantacao, checklistValues.dadosImovel?.tipoImplantacao || ''),
          // Converter para string para compatibilidade com Select do barihaus
          quantidadeQuartos: toStringValue(preserveOrDefault(data.dadosImovel?.quantidadeQuartos, checklistValues.dadosImovel?.quantidadeQuartos ?? 0)) as any,
          quantidadeBanheiros: toStringValue(preserveOrDefault(data.dadosImovel?.quantidadeBanheiros, checklistValues.dadosImovel?.quantidadeBanheiros ?? 0)) as any,
          quantidadeVagas: toStringValue(preserveOrDefault(data.dadosImovel?.quantidadeVagas, checklistValues.dadosImovel?.quantidadeVagas ?? 0)) as any,
          idadeAparente: preserveOrDefault(data.dadosImovel?.idadeAparente, checklistValues.dadosImovel?.idadeAparente ?? 0),
          estadoConservacao: preserveOrDefault(data.dadosImovel?.estadoConservacao, checklistValues.dadosImovel?.estadoConservacao || ''),
          padraoAcabamento: preserveOrDefault(data.dadosImovel?.padraoAcabamento, checklistValues.dadosImovel?.padraoAcabamento || ''),
          // Valor salvo só é preservado se for uma opção válida (o fator numérico antigo é descartado).
          fatorLiquidez: parseLiquidez(data.dadosImovel?.fatorLiquidez) ?? checklistValues.dadosImovel?.fatorLiquidez,
        },
        dadosTerreno: {
          ...data.dadosTerreno,
          areaTerreno: preserveOrDefault(data.dadosTerreno?.areaTerreno, firstItem?.landArea || 0),
          testada: preserveOrDefault(data.dadosTerreno?.testada, checklistValues.dadosTerreno?.testada ?? 0),
          padraoTerreno: preserveOrDefault(data.dadosTerreno?.padraoTerreno, checklistValues.dadosTerreno?.padraoTerreno || ''),
          topografia: preserveOrDefault(data.dadosTerreno?.topografia, checklistValues.dadosTerreno?.topografia || ''),
        },
        dadosCondominio: {
          areaComum: preserveOrDefault(data.dadosCondominio?.areaComum, checklistValues.dadosCondominio?.areaComum ?? 0),
          estadoConservacao: preserveOrDefault(data.dadosCondominio?.estadoConservacao, checklistValues.dadosCondominio?.estadoConservacao || ''),
        },
        _initialized: true, // Marca que os defaults já foram carregados
      };

      // Aplica mudanças do usuário que foram bufferizadas durante o carregamento
      const userChanges = userChangesRef.current;
      if (Object.keys(userChanges).length > 0) {
        if (userChanges['dadosImovel']) {
          merged.dadosImovel = { ...merged.dadosImovel, ...userChanges['dadosImovel'] };
        }
        if (userChanges['dadosTerreno']) {
          merged.dadosTerreno = { ...merged.dadosTerreno, ...userChanges['dadosTerreno'] };
        }
        if (userChanges['dadosCondominio']) {
          merged.dadosCondominio = { ...merged.dadosCondominio, ...userChanges['dadosCondominio'] };
        }
        // Limpa o buffer
        userChangesRef.current = {};
      }

      onUpdate(merged);
      form.setFieldsValue({
        ...merged,
        dadosImovel: {
          ...merged.dadosImovel,
          areaConstruida: merged.dadosImovel?.areaConstruida ?? undefined,
          quantidadeQuartos: merged.dadosImovel?.quantidadeQuartos ? String(merged.dadosImovel.quantidadeQuartos) : undefined,
          quantidadeBanheiros: merged.dadosImovel?.quantidadeBanheiros ? String(merged.dadosImovel.quantidadeBanheiros) : undefined,
          quantidadeVagas: merged.dadosImovel?.quantidadeVagas ? String(merged.dadosImovel.quantidadeVagas) : undefined,
          idadeAparente: merged.dadosImovel?.idadeAparente ?? undefined,
          fatorLiquidez: parseLiquidez(merged.dadosImovel?.fatorLiquidez),
        },
        dadosTerreno: {
          ...merged.dadosTerreno,
          areaTerreno: merged.dadosTerreno?.areaTerreno || undefined,
          testada: merged.dadosTerreno?.testada || undefined,
        },
        dadosCondominio: {
          ...merged.dadosCondominio,
          areaComum: merged.dadosCondominio?.areaComum || undefined,
        },
      });
      // Atualiza fullDataRef com os dados COMPLETOS
      fullDataRef.current = merged;
      // Habilita sincronização IMEDIATAMENTE (sem setTimeout)
      isInitializedRef.current = true;
    } catch {
      // Mesmo com erro, habilita sync e marca como inicializado
      onUpdate({ _initialized: true } as Partial<AvaliacaoData>);
      isInitializedRef.current = true;
    } finally {
      // Sempre marca que terminou de carregar
      isLoadingDefaultsRef.current = false;
    }
  };

  return (
    <div className="avaliacao-step">
      <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
        Dados Técnicos do Imóvel
      </h4>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
        Preencha as características técnicas do imóvel vistoriado. Campos marcados com * são
        obrigatórios.
      </p>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={handleValuesChange}
      >
        {/* ==================== Dados do Imóvel ==================== */}
        <h5 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
          Dados do Imóvel
        </h5>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item
            label="Tipo de Imóvel *"
            name={['dadosImovel', 'tipoImovel']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.tipoImovel"
              fieldLabel="Tipo de Imóvel"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select options={tipoImovelOptions} placeholder="Selecione o tipo" disabled={!isFieldEnabled('avaliacao.dadosImovel.tipoImovel')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Tipo de Implantação *"
            name={['dadosImovel', 'tipoImplantacao']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.tipoImplantacao"
              fieldLabel="Tipo de Implantação"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select options={TIPO_IMPLANTACAO_OPTIONS} placeholder="Selecione" disabled={!isFieldEnabled('avaliacao.dadosImovel.tipoImplantacao')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label={<>Área Construída (m<sup>2</sup>) {!isTerreno && '*'}</>}
            name={['dadosImovel', 'areaConstruida']}
            rules={[{ required: !isTerreno, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.areaConstruida"
              fieldLabel="Área Construída"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Input type="number" min={0} step={0.01} placeholder="Ex: 120.50" disabled={!isFieldEnabled('avaliacao.dadosImovel.areaConstruida')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Quantidade de Quartos *"
            name={['dadosImovel', 'quantidadeQuartos']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.quantidadeQuartos"
              fieldLabel="Quantidade de Quartos"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select
                options={[
                  { label: '0', value: '0', disabled: false },
                  ...[...Array(10)].map((_, i) => ({
                    label: String(i + 1),
                    value: String(i + 1),
                    disabled: false,
                  })),
                ]}
                placeholder="Selecione"
                disabled={!isFieldEnabled('avaliacao.dadosImovel.quantidadeQuartos')}
              />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Quantidade de Banheiros *"
            name={['dadosImovel', 'quantidadeBanheiros']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.quantidadeBanheiros"
              fieldLabel="Quantidade de Banheiros"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select
                options={[
                  { label: '0', value: '0', disabled: false },
                  ...[...Array(10)].map((_, i) => ({
                    label: String(i + 1),
                    value: String(i + 1),
                    disabled: false,
                  })),
                ]}
                placeholder="Selecione"
                disabled={!isFieldEnabled('avaliacao.dadosImovel.quantidadeBanheiros')}
              />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Quantidade de Vagas *"
            name={['dadosImovel', 'quantidadeVagas']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.quantidadeVagas"
              fieldLabel="Quantidade de Vagas"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select
                options={[
                  { label: '0 (Nenhuma)', value: '0', disabled: false },
                  ...[...Array(10)].map((_, i) => ({
                    label: String(i + 1),
                    value: String(i + 1),
                    disabled: false,
                  })),
                ]}
                placeholder="Selecione"
                disabled={!isFieldEnabled('avaliacao.dadosImovel.quantidadeVagas')}
              />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label={`Idade Aparente (anos) ${!isTerreno ? '*' : ''}`}
            name={['dadosImovel', 'idadeAparente']}
            rules={[{ required: !isTerreno, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.idadeAparente"
              fieldLabel="Idade Aparente"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Input type="number" min={0} placeholder="Ex: 5" disabled={!isFieldEnabled('avaliacao.dadosImovel.idadeAparente')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label={`Estado de Conservação ${!isTerreno ? '*' : ''}`}
            name={['dadosImovel', 'estadoConservacao']}
            rules={[{ required: !isTerreno, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.estadoConservacao"
              fieldLabel="Estado de Conservação"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select options={ESTADO_CONSERVACAO_OPTIONS} placeholder="Selecione" disabled={!isFieldEnabled('avaliacao.dadosImovel.estadoConservacao')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Padrão de Acabamento *"
            name={['dadosImovel', 'padraoAcabamento']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.padraoAcabamento"
              fieldLabel="Padrão de Acabamento"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select options={PADRAO_ACABAMENTO_OPTIONS} placeholder="Selecione" disabled={!isFieldEnabled('avaliacao.dadosImovel.padraoAcabamento')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Liquidez *"
            name={['dadosImovel', 'fatorLiquidez']}
            rules={[{ required: true, message: LIQUIDEZ_OBRIGATORIA_MENSAGEM }]}
            validateStatus={avisarLiquidezVazia ? 'warning' : undefined}
            help={avisarLiquidezVazia ? LIQUIDEZ_OBRIGATORIA_MENSAGEM : undefined}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosImovel.fatorLiquidez"
              fieldLabel="Liquidez"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection || liberarLiquidezNaCorrecao}
            >
              <Select
                options={LIQUIDEZ_OPTIONS}
                placeholder="Selecione a liquidez"
                disabled={!isFieldEnabled('avaliacao.dadosImovel.fatorLiquidez') && !liberarLiquidezNaCorrecao}
              />
            </ReviewFieldWrapper>
          </Form.Item>
        </div>

        <Divider />

        {/* ==================== Dados do Terreno ==================== */}
        <h5 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
          Dados do Terreno
        </h5>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item
            label={<>Área do Terreno (m<sup>2</sup>)</>}
            name={['dadosTerreno', 'areaTerreno']}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosTerreno.areaTerreno"
              fieldLabel="Área do Terreno"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Input type="number" min={0} step={0.01} placeholder="Ex: 250.00" disabled={!isFieldEnabled('avaliacao.dadosTerreno.areaTerreno')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Testada (m)"
            name={['dadosTerreno', 'testada']}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosTerreno.testada"
              fieldLabel="Testada"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Input type="number" min={0} step={0.01} placeholder="Ex: 10.00" disabled={!isFieldEnabled('avaliacao.dadosTerreno.testada')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Padrão Terreno *"
            name={['dadosTerreno', 'padraoTerreno']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosTerreno.padraoTerreno"
              fieldLabel="Padrão Terreno"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select options={PADRAO_TERRENO_OPTIONS} placeholder="Selecione" disabled={!isFieldEnabled('avaliacao.dadosTerreno.padraoTerreno')} />
            </ReviewFieldWrapper>
          </Form.Item>

          <Form.Item
            label="Topografia *"
            name={['dadosTerreno', 'topografia']}
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <ReviewFieldWrapper
              fieldPath="avaliacao.dadosTerreno.topografia"
              fieldLabel="Topografia"
              stepName="Avaliação"
              allowEditAllInCorrection={hasTipoImovelCorrection}
            >
              <Select options={TOPOGRAFIA_OPTIONS} placeholder="Selecione" disabled={!isFieldEnabled('avaliacao.dadosTerreno.topografia')} />
            </ReviewFieldWrapper>
          </Form.Item>
        </div>

        {isCondominio && (
          <>
            <Divider />

            {/* ==================== Dados do Condomínio ==================== */}
            <h5 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
              Dados do Condomínio
            </h5>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item
                label="Área Comum (m²)"
                name={['dadosCondominio', 'areaComum']}
              >
                <ReviewFieldWrapper
                  fieldPath="avaliacao.dadosCondominio.areaComum"
                  fieldLabel="Área Comum"
                  stepName="Avaliação"
                  allowEditAllInCorrection={hasTipoImovelCorrection}
                >
                  <Input type="number" min={0} step={0.01} placeholder="Ex: 1000.00" disabled={!isFieldEnabled('avaliacao.dadosCondominio.areaComum')} />
                </ReviewFieldWrapper>
              </Form.Item>

              <Form.Item
                label="Estado de Conservação"
                name={['dadosCondominio', 'estadoConservacao']}
              >
                <ReviewFieldWrapper
                  fieldPath="avaliacao.dadosCondominio.estadoConservacao"
                  fieldLabel="Estado de Conservação (Condomínio)"
                  stepName="Avaliação"
                  allowEditAllInCorrection={hasTipoImovelCorrection}
                >
                  <Select options={ESTADO_CONSERVACAO_OPTIONS} placeholder="Selecione" disabled={!isFieldEnabled('avaliacao.dadosCondominio.estadoConservacao')} />
                </ReviewFieldWrapper>
              </Form.Item>
            </div>
          </>
        )}
      </Form>
    </div>
  );
}
